import {Box, Typography} from "@material-ui/core";
import React, {FC, useMemo} from "react";
import {T, useTranslate} from "@tolgee/react";
import formatPrice from "tools/formats/formatPrice";
import {FullProduct} from "ts/api/product";
import {uniqBy} from "lodash";

import {BasicInput} from "view/components/Form";
import {Currency, IShow} from "@evennt-tech/api-v2-core";
import {calculateSupplierPaymentFeeAmount} from "view/apps/Exhibitor/pages/Cart/helpers";
import FlexBox from "view/components/Layout/FlexBox";
import ImportantIcon from "assets/ImportantIcon";
import {generateTestId} from "tools/cypress/generateTestId";
import {Alert} from "@mui/material";
import {MIN_AMOUNT_BY_CURRENCY} from "view/apps/Exhibitor/pages/Cart/components/Payment/shared";
import calculateTotalSupplierPrice from "tools/product/productCalculations/calculateTotalSupplierPrice";
import SupplierProductRow from "../SupplierProductRow";
import {useStyles} from "./style";

type Props = {
  products: FullProduct[];
  show: IShow;
  requireApproval?: boolean;
  showPoNumber?: boolean;
};

const SupplierProductsBlock: FC<Props> = ({
  products,
  show,
  requireApproval,
  showPoNumber = true,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const firstProduct = products[0];
  const supplierCompany = products[0].owner;

  const supplierPaymentFeeAmount = useMemo(
    () => calculateSupplierPaymentFeeAmount(products),
    [products]
  );

  const MIN_BANK_TRANSFER_INVOICE_AMOUNT = useMemo(() => {
    return MIN_AMOUNT_BY_CURRENCY[show.currency];
  }, [show]);

  const {totalAmount: totalSupplierPrice, discountAmount} = useMemo(() => {
    return calculateTotalSupplierPrice(
      products,
      show,
      supplierPaymentFeeAmount
    );
  }, [products, show, supplierPaymentFeeAmount]);
  const totalSupplierPriceLabel = t(
    "exhibitor.cart.bank_transfer.total_inc_tax",
    {
      taxName: show.salesTaxName,
      amount: formatPrice(totalSupplierPrice, show.currency),
    }
  );
  const discountAmountLabel = discountAmount
    ? t("exhibitor.cart.bank_transfer.discount_amount", {
        amount: formatPrice(discountAmount, show.currency),
      })
    : null;
  const poNumberInputName = `poNumbers.${supplierCompany.id}_${firstProduct.id}`;

  return (
    <Box key={supplierCompany.id} className={classes.root}>
      <Box className={classes.header}>
        <FlexBox gap={1} alignCenter>
          {requireApproval && <ImportantIcon width={24} height={24} />}
          <Typography className={classes.supplierCompanyNameTitle}>
            {supplierCompany.name}
          </Typography>
        </FlexBox>
        {showPoNumber && (
          <BasicInput
            {...generateTestId("exhibitor-cart-po-field")}
            className={classes.poNumberInput}
            label={t("exhibitor.cart.bank_transfer.po_number")}
            name={poNumberInputName}
            required={false}
          />
        )}
      </Box>
      <Box className={classes.productsContainer}>
        <Products products={products} />
        <Fees products={products} currency={show.currency} />
        <PaymentFee
          supplierPaymentFeeAmount={supplierPaymentFeeAmount}
          currency={show.currency}
        />
      </Box>
      {discountAmountLabel && (
        <Box className={classes.totalSupplierPrice}>{discountAmountLabel}</Box>
      )}
      <Box className={classes.totalSupplierPrice}>
        {totalSupplierPriceLabel}
      </Box>
      {totalSupplierPrice < MIN_BANK_TRANSFER_INVOICE_AMOUNT &&
        !discountAmount && (
          <Alert severity="error" className={classes.alert}>
            <T
              keyName="exhibitor.cart.bank_transfer.min_amount"
              params={{
                amount: formatPrice(
                  MIN_BANK_TRANSFER_INVOICE_AMOUNT,
                  show.currency
                ),
              }}
            />
          </Alert>
        )}
    </Box>
  );
};

const Products = ({products}: {products: FullProduct[]}) => {
  const renderProduct = (product: FullProduct) => {
    return (
      <>
        <SupplierProductRow key={product.id} product={product} />
        {product?.extras &&
          product?.extras?.length > 0 &&
          product.extras.map((extraProduct) => (
            <SupplierProductRow
              key={extraProduct.id}
              isExtra
              product={extraProduct}
            />
          ))}
      </>
    );
  };
  return <>{products.map(renderProduct)}</>;
};

const PaymentFee = ({
  supplierPaymentFeeAmount,
  currency,
}: {
  supplierPaymentFeeAmount: number;
  currency: Currency;
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.feesContainer}>
      <Box className={classes.feeItem}>
        <Box>
          <T keyName="exhibitor.cart.bank_transfer.payment_fee" />
        </Box>
        <Box>{formatPrice(supplierPaymentFeeAmount, currency)}</Box>
      </Box>
    </Box>
  );
};

const Fees = ({
  products,
  currency,
}: {
  products: FullProduct[];
  currency: Currency;
}) => {
  const classes = useStyles();

  const fees = uniqBy(products.map((product) => product?.fees).flat(1), "id");
  if (!fees.length) {
    return null;
  }
  return (
    <Box className={classes.feesContainer}>
      {fees.map((fee) => (
        <Box key={fee.id} className={classes.feeItem}>
          <Box>{fee.name}</Box>
          <Box>{formatPrice(fee.amountWithFees, currency)}</Box>
        </Box>
      ))}
    </Box>
  );
};
export default SupplierProductsBlock;
